// Ensure that `auth`, `db`, and `storage` are correctly imported from your Firebase configuration
import { auth, db, storage } from "../../firebaseConfig";
import { doc, setDoc, getDoc } from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/**
 * Uploads a given File object to Firebase Storage and returns its download URL.
 * @param {File} file - The file to upload.
 * @param {string} uploadPath - The path in Firebase Storage where the file will be stored.
 * @returns {Promise<string>} - The download URL of the uploaded file.
 */
const uploadFileToStorage = async (file, uploadPath) => {
  try {
    const storageRef = ref(storage, uploadPath);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    // console.error("Error uploading file to storage:", file.name, error);
    throw new Error("Failed to upload file to storage");
  }
};

/**
 * Deletes a file from Firebase Storage given its full path.
 * @param {string} filePath - The full path of the file in Firebase Storage.
 * @returns {Promise<void>}
 */
const deleteFileFromStorage = async (filePath) => {
  try {
    const fileRef = ref(storage, filePath);
    await deleteObject(fileRef);
  } catch (error) {
    // console.error(`Error deleting file at ${filePath}`, error);
    // Not throwing here because we may want to continue with other deletions.
  }
};

/**
 * Saves Section 9 form data to Firestore after handling file uploads and deletions.
 *
 * The logic is as follows:
 * 1. Retrieve previously saved files from Firestore.
 * 2. Determine which files need to be deleted (those no longer present in the new formData).
 * 3. Delete those files from Firebase Storage.
 * 4. Upload any new files and get their download URLs.
 * 5. Save the updated form data to Firestore, including the file URLs.
 *
 * @param {Object} formData - The entire Section 9 form data object, including:
 *   - `files`: An object keyed by document sections, each containing an array of File objects or file references.
 *   - `generalFiles`: An array of File objects or file references.
 * @returns {Promise<void>}
 */
export const writeDataS9 = async (formData) => {
  const user = auth.currentUser;
  if (!user) {
    // console.error("User not authenticated. Cannot write Section 9 data.");
    return;
  }

  const docRef = doc(
    db,
    "Advisers",
    user.uid,
    "Application Details",
    "Additional Information"
  );

  try {
    // Step 1: Retrieve previously saved data (specifically the file info) from Firestore
    const docSnap = await getDoc(docRef);
    const previousData = docSnap.exists() ? docSnap.data() : {};
    const previouslySavedFiles = previousData.files || {};

    // Step 2: Identify files that need to be deleted
    // Files need deletion if:
    // - The key (document section) no longer exists in formData.files
    // - A file previously saved is not present in the new formData.files array
    const filesToDelete = [];

    for (const documentKey in previouslySavedFiles) {
      const oldFilesArray = previouslySavedFiles[documentKey] || [];
      const newFilesArray = formData.files[documentKey] || [];

      // Create a set of current file names for easy checking
      const currentFileNames = newFilesArray.map((f) => f.name);

      oldFilesArray.forEach((oldFile) => {
        if (!currentFileNames.includes(oldFile.name)) {
          // This old file is not found in the new data and should be deleted
          filesToDelete.push(oldFile);
        }
      });
    }

    // Step 3: Delete identified files from Firebase Storage
    await Promise.all(
      filesToDelete.map((file) => {
        const filePath = `uploads/${user.uid}/ApplicationDetails/AdditionalInformation/${file.name}`;
        return deleteFileFromStorage(filePath);
      })
    );

    // Step 4: Upload any new files that are not already URLs (meaning they are File objects)
    const uploadedFileUrls = {};

    for (const documentKey in formData.files) {
      if (!Object.prototype.hasOwnProperty.call(formData.files, documentKey))
        continue;

      // Upload new files for this documentKey
      uploadedFileUrls[documentKey] = await Promise.all(
        formData.files[documentKey].map(async (file) => {
          if (file instanceof File) {
            // Upload a new file
            const filePath = `uploads/${user.uid}/ApplicationDetails/AdditionalInformation/${file.name}`;
            const fileUrl = await uploadFileToStorage(file, filePath);
            return { name: file.name, url: fileUrl };
          } else {
            // Already an object with {name, url}, just retain it
            return file;
          }
        })
      );
    }

    // Handle generalFiles in the same manner
    const uploadGeneralFiles = await Promise.all(
      (formData.generalFiles || []).map(async (file) => {
        if (file instanceof File) {
          const filePath = `uploads/${user.uid}/ApplicationDetails/AdditionalInformation/${file.name}`;
          const fileUrl = await uploadFileToStorage(file, filePath);
          return { name: file.name, url: fileUrl };
        } else {
          return file;
        }
      })
    );

    // Step 5: Prepare the data object to save, merging the uploaded file URLs
    const dataToSave = {
      ...formData,
      files: uploadedFileUrls,
      generalFiles: uploadGeneralFiles,
    };

    // Step 6: Write the updated data to Firestore
    await setDoc(docRef, dataToSave, { merge: true });
    // console.log("Section 9 data successfully written!");
  } catch (error) {
    // console.error("Error writing Section 9 data to Firestore:", error);
  }
};
